import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../services/user.service';
import { AuthService } from '../auth/auth.service';
import { TenantService } from '../services/tenant.service';
import { LoggerService } from '../services/logger.service';
import { Subscription } from 'rxjs';

type ModalMode = 'create' | 'edit' | 'activate' | 'approve';
type FilterState = 'todos' | 'activo' | 'inactivo' | 'pendiente';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  allUsers: any[] = [];
  currentFilter: FilterState = 'activo';
  
  selectedUser: any = null;
  isRoleDrawerOpen: boolean = false;
  showModal: boolean = false;
  modalMode: ModalMode = 'create';
  
  isSuperAdminAppUser: boolean = false; 
  isGlobalMode: boolean = false; // NUEVO: Para el control de la vista Global
  tenants: any[] = [];
  userForm: FormGroup;
  private formSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    public authService: AuthService,
    private tenantService: TenantService,
    private logger: LoggerService
  ) {
    this.userForm = this.fb.group({
      username: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [''],
      totaltokens: [0, [Validators.required, Validators.min(0)]],
      consumedtokens: [0, [Validators.required, Validators.min(0)]],
      tenantid: [null], 
      issuperadmin: [false]
    });
  }

  ngOnInit(): void {
    this.isSuperAdminAppUser = this.authService.isSuperAdmin();
    
    // NUEVO: Calculamos si estamos en modo global (Centro de control)
    this.isGlobalMode = this.isSuperAdminAppUser && !this.authService.getActiveTenantId();

    this.refreshUsers();
    
    if (this.isSuperAdminAppUser) {
      this.tenantService.getAllTenants().subscribe({
        next: (data: any[]) => this.tenants = data,
        error: (err: any) => this.logger.error('Error cargando tenants', err)
      });
    }

    this.formSub = this.userForm.get('issuperadmin')!.valueChanges.subscribe(isSuper => {
      const tenantCtrl = this.userForm.get('tenantid');
      if (this.isSuperAdminAppUser) {
        if (isSuper) tenantCtrl?.clearValidators(); 
        else tenantCtrl?.setValidators([Validators.required]); 
        tenantCtrl?.updateValueAndValidity();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.formSub) this.formSub.unsubscribe();
  }

  get filteredUsers(): any[] {
    if (this.currentFilter === 'todos') return this.allUsers;
    return this.allUsers.filter(u => u.estado === this.currentFilter);
  }

  get pendingCount(): number { return this.allUsers.filter(u => u.estado === 'pendiente').length; }
  get countActive(): number { return this.allUsers.filter(u => u.estado === 'activo').length; }
  get countInactive(): number { return this.allUsers.filter(u => u.estado === 'inactivo').length; }

  get modalTitle(): string {
    switch (this.modalMode) {
      case 'create': return 'NUEVO USUARIO';
      case 'edit': return 'EDITAR DATOS';
      case 'activate': return 'COMPLETAR Y ACTIVAR USUARIO';
      case 'approve': return 'COMPLETAR Y APROBAR SOLICITUD';
      default: return '';
    }
  }

  refreshUsers(): void {
    this.userService.getAllUsers().subscribe({
      next: (data: any[]) => this.allUsers = data,
      error: (err: any) => this.logger.error('Error cargando usuarios', err)
    });
  }

  selectUser(user: any): void {
    if (user.estado === 'activo') {
      this.selectedUser = user;
      this.isRoleDrawerOpen = true;
    }
  }

  closeRoleDrawer(): void { this.isRoleDrawerOpen = false; }

  openModal(mode: ModalMode, user?: any): void {
    this.modalMode = mode;
    this.showModal = true;
    
    if (user) {
      this.selectedUser = user;
      this.userForm.patchValue({
        username: user.username, email: user.email, password: '', 
        totaltokens: user.totaltokens || 0, consumedtokens: user.consumedtokens || 0,
        tenantid: user.tenantid || null, issuperadmin: user.issuperadmin || false
      });
      this.userForm.get('password')?.clearValidators();
    } else {
      this.selectedUser = null;
      this.userForm.reset({ totaltokens: 0, consumedtokens: 0, issuperadmin: false, tenantid: null });
      this.userForm.get('password')?.setValidators([Validators.required]);
    }
    
    this.userForm.get('issuperadmin')?.updateValueAndValidity();
    this.userForm.get('password')?.updateValueAndValidity();
  }

  closeModal(): void {
    this.showModal = false;
    this.userForm.reset();
  }

  saveUser(): void {
    if (this.userForm.invalid) return;
    
    const userData = { ...this.userForm.value };
    if (this.modalMode !== 'create' && !userData.password) delete userData.password;

    if (!this.isSuperAdminAppUser) {
      const currentUser = this.authService.getCurrentUser();
      userData.tenantid = currentUser ? currentUser.tenant?.id : null;
    }

    const isStateTransition = this.modalMode === 'activate' || this.modalMode === 'approve';

    const request$ = this.modalMode === 'create'
      ? this.userService.createUser(userData)
      : this.userService.updateUserById(this.selectedUser.id, userData);

    request$.subscribe({
      next: () => {
        if (isStateTransition) {
            this.userService.updateUserStatus(this.selectedUser.id, 'activo').subscribe({
                next: () => {
                    this.logger.log('Usuario completado y activado exitosamente (Rol verificado).');
                    this.finalizeSave();
                },
                error: (err: any) => this.logger.error('Error crítico al cambiar el estado a Activo', err)
            });
        } else {
            this.logger.log('Usuario guardado correctamente.');
            this.finalizeSave();
        }
      },
      error: (err: any) => this.logger.error('Error al guardar datos básicos del usuario', err)
    });
  }

  private finalizeSave(): void {
    this.refreshUsers();
    this.closeModal();
  }

  deactivate(user: any, event: Event): void {
    event.stopPropagation();
    if (confirm(`¿Inactivar al usuario ${user.username}? No podrá iniciar sesión.`)) {
      this.userService.updateUserStatus(user.id, 'inactivo').subscribe({
        next: () => {
          if (this.selectedUser?.id === user.id) this.isRoleDrawerOpen = false;
          this.refreshUsers();
        }
      });
    }
  }

  reject(user: any, event: Event): void {
    event.stopPropagation();
    if (confirm(`¿Denegar definitivamente la solicitud de ${user.username}?`)) {
      this.userService.updateUserStatus(user.id, 'rechazado').subscribe(() => this.refreshUsers());
    }
  }

  delete(user: any, event: Event): void {
    event.stopPropagation();
    if (confirm(`¿ELIMINAR PERMANENTEMENTE a ${user.username}? Esta acción es irreversible.`)) {
      this.userService.deleteUser(user.id).subscribe({
        next: () => {
          if (this.selectedUser?.id === user.id) this.isRoleDrawerOpen = false;
          this.refreshUsers();
        }
      });
    }
  }
}